import { Button } from '@ordo/ui/button'
import { ArrowLeft } from 'lucide-react'
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router'

/**
 * Back-to-index navigation button used in detail/new/edit page headers.
 *
 * Uses `navigate(-1)` when there is a history entry to pop back to — this
 * preserves URL query state (e.g. nuqs-backed DataGrid filters) so the
 * user returns to the list page exactly as they left it. When there's no
 * history entry (direct link, fresh tab, page reload) it falls back to
 * the explicit `to` prop.
 */
export function BackLink({ to, children }: { to: string; children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()

  const handleClick = useCallback(() => {
    // When react-router has no previous entry under its control, `key` is
    // "default". In that case navigate(-1) would leave the app, so route
    // to the explicit fallback instead.
    if (location.key === 'default') {
      void navigate(to)
    } else {
      void navigate(-1)
    }
  }, [navigate, to, location.key])

  return (
    <Button variant="ghost" size="sm" onClick={handleClick} className="mb-2 text-muted-foreground">
      <ArrowLeft className="h-4 w-4" />
      {children}
    </Button>
  )
}
